import { useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ConsentPlaceholder, RichText } from 'ui/components';
import { DictionaryContext } from 'application/adapters/context/Dictionary';
import { useYouTubeIframeConsent } from 'application/hooks/useYouTubeIframeConsent';
import { deferYouTubeIframes } from 'helpers/youtubeEmbed';

export type EditorialRichTextProps = Omit<RichText, 'richTextRef'>;

// RichText for editor-written CMS HTML. YouTube iframes only load with consent; otherwise a ConsentPlaceholder is shown
export const EditorialRichText: React.FC<EditorialRichTextProps> = ({ content, ...props }) => {
	const ref = useRef<HTMLDivElement>(null);
	const dictionary = useContext(DictionaryContext);

	// Deferred on the server too, so YouTube iframes never load before consent is checked
	const deferredContent = deferYouTubeIframes(content);
	const consentPlaceholderTargets = useYouTubeIframeConsent(ref, deferredContent);

	return (
		<>
			<RichText {...props} content={deferredContent} richTextRef={ref} />
			{consentPlaceholderTargets.map(({ element, fillsContainer }, index) =>
				createPortal(
					<ConsentPlaceholder
						heading={dictionary.getValue('Video.ConsentHeading', null, 'Video unavailable')}
						text={dictionary.getValue(
							'Video.ConsentText',
							null,
							'To watch this video, please update your cookie preferences in the bottom-left corner of the page.',
						)}
						fillContainer={fillsContainer}
					/>,
					element,
					String(index),
				),
			)}
		</>
	);
};
